import React, { useEffect, useMemo, useState } from "react";
import styles from "./suggestedPrompts.module.css";
import * as SuggestedIconsIndex from "shared/assets/svg/suggested-prompts/SuggestedPromptsIndex";
import { RedDotIcon } from "shared/assets/svg/menu-icons/RedDotIcon";
import { WrappersService } from "shared/services/WrappersService";

/**
 * v16 of `@dev-streetbeat/chat` removed `useStarterConversations` from the curated
 * public surface (`@dev-streetbeat/chat/react`). The internal implementation and
 * the API service method `getStarterConversations()` still exist, but neither is
 * publicly importable through the package's `exports` map.
 *
 * Until the feature is reinstated upstream (or replaced with a direct fetch against
 * the `/starter_conversations` HTTP route), the prompt list is hardcoded here. The
 * content matches the values the API previously returned for the Fineco assistant.
 */
type StarterConversation = { content: string; icon: string; badge?: 'wrappers' };

// NOTE: the grid layout (gridClass below) supports at most 6 cards — a 7th has no layout.
const HARDCODED_STARTER_CONVERSATIONS: StarterConversation[] = [
  { icon: 'SuggestedPromptIcon1', content: 'Aiutami a creare un portafoglio personalizzato in pochi step' },
  { icon: 'SuggestedPromptIcon4', content: 'Carica un portafoglio manualmente, analizza e genera report' },
  { icon: 'SuggestedPromptIcon3', content: 'Fammi una sintesi delle principali notizie di mercato di oggi' },
  { icon: 'SuggestedPromptIcon2', content: 'Carica la mia lista di strumenti preferiti e crea un portafoglio' },
  // Wrappers (FAM funds of funds, issue #62): `badge` drives the "new monthly feed" red dot.
  { icon: 'SuggestedPromptIcon4', content: 'Consulta le tipologie di wrapper, visualizza e stampa la scheda prodotto', badge: 'wrappers' },
];

const useStarterConversations = (): {
  starterConversations: StarterConversation[];
  loading: boolean;
  error: null;
} => ({
  starterConversations: HARDCODED_STARTER_CONVERSATIONS,
  loading: false,
  error: null,
});


type SuggestedPromptsProps = {
  onSend?: (text: string) => void;
};


export const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({
  onSend,
}) => {
  const { starterConversations, loading, error } = useStarterConversations();
  let forceStyleGuide = false;

  // Red dot on the wrappers card: a newer FAM feed than the one this user last opened
  // (server-side per user — GET /fineco/wrappers/status). Silent on failure.
  const [wrappersUpdate, setWrappersUpdate] = useState(false);
  useEffect(() => {
    WrappersService.getStatus()
      .then((status) => setWrappersUpdate(Boolean(status?.has_update)))
      .catch(() => setWrappersUpdate(false));
  }, []);

  const handleClick = (message: StarterConversation) => {
    if (message.badge === 'wrappers') {
      setWrappersUpdate(false);
      WrappersService.markSeen().catch(() => undefined);
    }
    onSend?.(message.content);
  };

  const suggestedPromptsList = starterConversations || [];

  const gridClass = useMemo(() => {
    const itemCount = suggestedPromptsList.length;

    if (forceStyleGuide) return styles.gridTriple;
    if (itemCount <= 1) return styles.gridSingle;
    if (itemCount <= 4) return styles.gridDouble;
    if (itemCount <= 6) return styles.gridTriple;
  }, [suggestedPromptsList.length]);

  const getIconComponent = (iconName: string) => {
    const { SuggestedPromptIcon4 } = SuggestedIconsIndex;

    if (!iconName || typeof iconName !== 'string') {
      return <SuggestedPromptIcon4 className={styles.iconPrompt} />;
    }

    const IconComponent =
      SuggestedIconsIndex[iconName as keyof typeof SuggestedIconsIndex];
    
    if (IconComponent) {
      return <IconComponent className={styles.iconPrompt} />;
    }

    return <SuggestedPromptIcon4 className={styles.iconPrompt} />;
  };

  if (loading || !suggestedPromptsList.length) {
    return null;
  }

  if (error) {
    return null;
  }


  return (
    <div className={styles.wrapper}>
      <div className={`${styles.gridContainer} ${gridClass}`}>
        {suggestedPromptsList.map((message, idx) => (
          <div
            key={`init_${idx}`}
            className={styles.gridItemContainer}
            onClick={() => handleClick(message)}
          >
            {message.badge === 'wrappers' && wrappersUpdate && (
              <div className={styles.redDot}>
                <RedDotIcon />
              </div>
            )}
            <div className={styles.gridItem}>
              {getIconComponent(message.icon)}
              {message.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuggestedPrompts;
